import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Rocket, BookOpen, GraduationCap, Stethoscope, Heart, Brain, 
  Pill, Baby, Users, ChevronLeft, Search, Award, Clock, 
  CheckCircle, XCircle, Loader2, CreditCard, Crown, Play,
  BarChart3, Target, Zap, Star, Menu, X, Home,
  Volume2, Pause, Mic, Headphones, Scale, Building2, Dna, Presentation, Plus, LogOut
} from 'lucide-react';
import { ExamModeSelector, AdaptiveExamView, ExamResultsView, UserDashboard } from './components/AdaptiveExam';
import { EnhancedDashboard } from './components/EnhancedDashboard';
import FullSimulator from './components/FullSimulator';
import BiologyPresentation from './components/BiologyPresentation';
import { TalkingPresentationSection } from './components/TalkingPresentationSection';
import MedicalSimulator from './components/MedicalSimulator';
import NursingSimulator from './components/NursingSimulator';
import DentalSimulator from './components/DentalSimulator';
import PharmacySimulator from './components/PharmacySimulator';
import AdminPanel from './components/AdminPanel';
import { PricingModal, SubscriptionBanner } from './components/Pricing';
import { AuthCallback, LoginButton, LogoutButton, useAuth } from './components/Auth';
import UserProfile from './components/UserProfile';
import AppHeader from './components/AppHeader';
import FloatingContactButton from './components/FloatingContactButton';
import AIAssistant from './components/AIAssistant';
import WelcomeSection from './components/WelcomeSection';
import { SpecializationGrid, LicenseExamsSection } from './components/SpecializationGrid';
import { PromoView, RegisterView, QuizView, GovExamView, ExamResults, SpecializationView, CourseDetailView } from './views';
import ContributorsWall from './components/ContributorsWall';
import InstallAppBanner from './components/InstallAppBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Add /api prefix to all API calls
const API = `${BACKEND_URL}/api`;

// Local Storage Keys
const SESSION_KEY = 'chaser_user_session';
const APP_VERSION = '4.1.0';

// Course Icons Mapping
const COURSE_ICONS = {
  'internal-medicine': Stethoscope,
  'surgery': Heart,
  'pediatrics': Baby,
  'maternity': Users,
  'mental-health': Brain,
  'pharmacology': Pill,
  'anatomy': BookOpen,
  'community': Users,
};

// Main App Component
export default function App() {
  // Check for OAuth callback first (synchronous check during render)
  // CRITICAL: This must happen before any useEffect to prevent race conditions
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback onAuthSuccess={(user) => {
      // Will navigate to home after successful auth
    }} />;
  }

  return <MainApp />;
}

// Main App Implementation (separated to handle auth callback cleanly)
function MainApp() {
  // Auth state from hook
  const { user: authUser, loading: authLoading, isAuthenticated, setUser: setAuthUser } = useAuth();
  
  // State
  const [view, setView] = useState('promo');
  const [user, setUser] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Quiz State
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizType, setQuizType] = useState('course');
  const [questionCounts, setQuestionCounts] = useState({});
  const [dynamicStats, setDynamicStats] = useState(null);

  // Adaptive Exam State
  const [adaptiveMode, setAdaptiveMode] = useState(null);
  const [adaptiveResults, setAdaptiveResults] = useState(null);
  const [showLicenseExams, setShowLicenseExams] = useState(false);
  const [quizLanguage, setQuizLanguage] = useState('he');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [courseVideos, setCourseVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [newVideoData, setNewVideoData] = useState({ title: '', description: '', vimeo_url: '', urls: '' });
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoCounts, setVideoCounts] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const videoFileRef = useRef(null);
  
  // Pricing State
  const [showPricing, setShowPricing] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [subscription, setSubscription] = useState(() => {
    const saved = localStorage.getItem('my_doctor_subscription');
    if (saved) {
      const sub = JSON.parse(saved);
      // Check if trial is still valid
      if (sub.type === 'trial' && sub.startDate) {
        const start = new Date(sub.startDate);
        const now = new Date();
        const daysElapsed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
        sub.daysLeft = Math.max(0, 21 - daysElapsed);
      }
      return sub;
    }
    return null;
  });

  // Track page visits
  const trackVisit = useCallback(async (page) => {
    try {
      await axios.post(`${API}/analytics/visit`, {
        page,
        user_id: user?.id || authUser?.id || null
      });
    } catch (e) {
      // Silent fail - analytics shouldn't break the app
    }
  }, [user, authUser]);

  // Track visits on view change
  useEffect(() => {
    trackVisit(view);
  }, [view, trackVisit]);

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    const newSubscription = {
      type: plan.id,
      plan: plan.name,
      price: plan.price,
      startDate: new Date().toISOString(),
      daysLeft: plan.id === 'trial' ? 21 : null
    };
    setSubscription(newSubscription);
    localStorage.setItem('my_doctor_subscription', JSON.stringify(newSubscription));
    setShowPricing(false);
  };

  // Load video counts per course
  const loadVideoCounts = async () => {
    try {
      const response = await axios.get(`${API}/videos/counts`);
      setVideoCounts(response.data);
    } catch (e) {
      console.error('Failed to load video counts:', e);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const userId = user?.id || authUser?.user_id;
      if (!userId) return;
      const response = await axios.get(`${API}/users/${userId}/recent-activity`);
      const data = response.data;
      const items = [
        ...(data.recent_sessions || []).map(s => ({
          course_id: s.course_id,
          course_name: s.course_name || s.mode || 'תרגול',
          specialization: s.specialization || data.specialization,
          questions_answered: s.questions_answered,
          score: s.correct_answers && s.questions_answered ? Math.round(s.correct_answers / s.questions_answered * 100) : null,
          date: s.started_at
        })),
        ...(data.recent_results || []).map(r => ({
          course_id: r.course_id,
          course_name: r.course_name || r.exam_type || 'מבחן',
          specialization: r.specialization || data.specialization,
          questions_answered: r.total_questions,
          score: r.percentage,
          exam_type: r.exam_type,
          date: r.completed_at
        }))
      ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 5);
      setRecentActivity(items);
    } catch (e) {
      // Silent fail
    }
  };

  // Load dynamic stats from API
  const loadDynamicStats = async () => {
    try {
      const [statsResponse, countsResponse] = await Promise.all([
        axios.get(`${API}/stats/dynamic`),
        axios.get(`${API}/questions/all-counts`)
      ]);
      setDynamicStats(statsResponse.data);
      // Merge per-specialization stats with per-course counts so
      // questionCounts[spec.id] and questionCounts[course.id] both resolve.
      setQuestionCounts({
        ...(statsResponse.data.by_specialization || {}),
        ...(countsResponse.data || {})
      });
    } catch (e) {
      console.error('Failed to load dynamic stats:', e);
      // Fallback stats if API fails
      setDynamicStats({
        total_questions: 19806,
        total_courses: 81,
        total_videos: 160
      });
    }
  };

  // Load question counts from API (legacy)
  const loadQuestionCounts = async () => {
    loadDynamicStats();
  };

  // Load course videos
  const loadCourseVideos = async (courseSlug) => {
    try {
      const response = await axios.get(`${API}/videos?course_slug=${courseSlug}`);
      setCourseVideos(response.data);
    } catch (e) {
      console.error('Failed to load course videos:', e);
      setCourseVideos([]);
    }
  };

  // Add multiple videos to course (batch)
  const handleAddVideo = async () => {
    if (!selectedCourse) return;
    
    const urls = newVideoData.urls
      .split('\n')
      .map(u => u.trim())
      .filter(u => u.length > 0);
    
    if (urls.length === 0) return;
    
    setUploadingVideo(true);
    setUploadProgress(0);
    
    try {
      await axios.post(`${API}/admin/videos/batch`, {
        urls,
        course_slug: selectedCourse.id,
        course_name: selectedCourse.name,
        category: selectedSpec?.id || 'general',
        start_order: courseVideos.length + 1
      });
      
      // Reload videos
      loadCourseVideos(selectedCourse.id);
      loadVideoCounts();
      setShowVideoUpload(false);
      setNewVideoData({ title: '', description: '', vimeo_url: '', urls: '' });
    } catch (e) {
      console.error('Failed to add videos:', e);
      alert('שגיאה בהוספת הסרטונים');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  // Delete a video
  const handleDeleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`);
      loadCourseVideos(selectedCourse.id);
      loadVideoCounts();
    } catch (e) {
      console.error('Failed to delete video:', e);
    }
  };

  // Upload video files from local machine
  const handleFileUpload = async (files) => {
    if (!selectedCourse || !files || files.length === 0) return;
    setUploadingVideo(true);
    setUploadProgress(0);
    const totalFiles = files.length;
    let uploaded = 0;

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('course_slug', selectedCourse.id);
        formData.append('course_name', selectedCourse.name);
        formData.append('category', selectedSpec?.id || 'general');
        formData.append('order', courseVideos.length + uploaded + 1);

        await axios.post(`${API}/admin/upload-video`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const fileProgress = (e.loaded / e.total) * 100;
            const totalProgress = ((uploaded / totalFiles) * 100) + (fileProgress / totalFiles);
            setUploadProgress(Math.round(totalProgress));
          }
        });
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
      }
      loadCourseVideos(selectedCourse.id);
      loadVideoCounts();
      setShowVideoUpload(false);
    } catch (e) {
      console.error('Failed to upload files:', e);
      alert('שגיאה בהעלאת הקבצים');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  // Move video to another course
  const handleMoveVideo = async (videoId, targetCourseSlug) => {
    try {
      await axios.patch(`${API}/admin/videos/${videoId}`, { course_slug: targetCourseSlug });
      loadCourseVideos(selectedCourse.id);
      loadVideoCounts();
    } catch (e) {
      console.error('Failed to move video:', e);
    }
  };

  // Session management - integrate with Google Auth
  useEffect(() => {
    // If authenticated via Google OAuth, use that user
    if (isAuthenticated && authUser) {
      const userData = {
        id: authUser.user_id,
        name: authUser.name,
        email: authUser.email,
        picture: authUser.picture,
        is_premium: authUser.is_premium,
        subscription_type: authUser.subscription_type
      };
      setUser(userData);
      setView('home');
      loadSpecializations();
      return;
    }
    
    // Fallback to legacy session
    const savedSession = localStorage.getItem(SESSION_KEY);
    if (savedSession) {
      try {
        const userData = JSON.parse(savedSession);
        setUser(userData);
        setView('home');
        loadSpecializations();
      } catch (e) {
        localStorage.removeItem(SESSION_KEY);
      }
    }
  }, [isAuthenticated, authUser]);

  const loadSpecializations = async () => {
    try {
      const response = await axios.get(`${API}/specializations`);
      setSpecializations(response.data);
      loadQuestionCounts();
      loadVideoCounts();
      loadRecentActivity();
    } catch (e) {
      console.error('Failed to load specializations from API:', e);
      // Try loading from static file as fallback
      try {
        const fallbackResponse = await axios.get('/data/specializations.json');
        setSpecializations(fallbackResponse.data);
        console.log('Loaded specializations from fallback');
      } catch (fallbackError) {
        console.error('Failed to load fallback:', fallbackError);
        // Last resort - minimal static data
        setSpecializations([
          { id: 'nursing', name: 'סיעוד', icon: '🏥', courses: [] },
          { id: 'education', name: 'חינוך', icon: '📚', courses: [] },
          { id: 'law', name: 'משפטים', icon: '⚖️', courses: [] },
          { id: 'gov-exams', name: 'מבחני רישוי', icon: '🎓', exams: [] }
        ]);
      }
    }
  };

  const fetchSpecializations = async () => {
    try {
      const response = await axios.get(`${API}/specializations`);
      return response.data;
    } catch (e) {
      console.error('Failed to load specializations:', e);
      return [];
    }
  };

  // Register user
  const registerUser = async (name, email, specId) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/users/register`, {
        name,
        email,
        specialization_id: specId
      }, { timeout: 10000 });
      
      const userData = response.data;
      setUser(userData);
      localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
      
      await loadSpecializations();
      setView('home');
    } catch (e) {
      const msg = e.response?.status === 520 
        ? 'השרת לא זמין כרגע. נסה שוב בעוד דקה.'
        : e.code === 'ECONNABORTED'
          ? 'השרת לא מגיב. בדוק את החיבור לאינטרנט.'
          : 'שגיאה בהרשמה, נסה שוב';
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // Start Quiz
  const startQuiz = async (type = 'course', difficulty = 'medium', courseId = null) => {
    setQuizLoading(true);
    setQuizType(type);
    setView('quiz');
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);

    try {
      let response;
      if (type === 'governmental') {
        const specId = selectedSpec?.id || 'nursing';
        response = await axios.get(`${API}/questions/governmental`, {
          params: { difficulty, count: 50, specialization: specId }
        });
      } else if (type === 'quick') {
        response = await axios.get(`${API}/questions/random`, {
          params: { count: 25, difficulty }
        });
      } else if (type === 'bar-exam') {
        // Bar Exam (בחינת לשכה) questions
        response = await axios.get(`${API}/questions/bar-exam`, {
          params: { 
            course_slug: courseId,
            difficulty,
            limit: 25 
          }
        });
      } else if (type === 'license-exam' || type === 'gov-exam') {
        // License exams (מבחני רישוי) - nursing, medical, dental, pharmacy
        response = await axios.get(`${API}/questions`, {
          params: { 
            course_slug: courseId,
            limit: 25 
          }
        });
      } else {
        response = await axios.get(`${API}/questions`, {
          params: { 
            course_slug: courseId || selectedCourse?.id,
            difficulty,
            limit: 25 
          }
        });
      }
      
      const questions = response.data;
      if (questions.length > 0) {
        setQuizQuestions(questions);
      } else {
        const fallback = await axios.get(`${API}/questions/random`, { params: { count: 25 } });
        setQuizQuestions(fallback.data);
      }
    } catch (e) {
      console.error('Failed to load questions:', e);
      setError('לא ניתן לטעון שאלות כרגע');
    } finally {
      setQuizLoading(false);
    }
  };

  // Start Chapter Quiz - quiz for specific topic/chapter
  const startChapterQuiz = async (chapter, courseId, difficulty = 'mixed') => {
    setQuizLoading(true);
    setQuizType('chapter');
    setView('quiz');
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);

    try {
      // Try to get questions for this specific topic with difficulty
      const params = { 
        course_id: courseId,
        topic: chapter.name,
        limit: 25 
      };
      if (difficulty && difficulty !== 'mixed') {
        params.difficulty = difficulty;
      }
      
      const response = await axios.get(`${API}/questions/by-topic`, { params });
      
      let questions = response.data;
      
      // If not enough questions for topic, get general course questions
      if (questions.length < 10) {
        const fallbackParams = { course_id: courseId, limit: 25 };
        if (difficulty && difficulty !== 'mixed') {
          fallbackParams.difficulty = difficulty;
        }
        const fallback = await axios.get(`${API}/questions`, { params: fallbackParams });
        questions = fallback.data;
      }
      
      if (questions.length > 0) {
        setQuizQuestions(questions);
      } else {
        const fallback = await axios.get(`${API}/questions/random`, { params: { count: 25 } });
        setQuizQuestions(fallback.data);
      }
    } catch (e) {
      console.error('Failed to load chapter questions:', e);
      // Fallback to course questions
      try {
        const fallback = await axios.get(`${API}/questions`, {
          params: { course_id: courseId, limit: 25 }
        });
        setQuizQuestions(fallback.data);
      } catch (e2) {
        setError('לא ניתן לטעון שאלות כרגע');
      }
    } finally {
      setQuizLoading(false);
    }
  };

  // Start Gov Exam Chapter Quiz - for chapter-based practice in licensing exams
  const startGovChapterQuiz = async (examId, chapterId, difficulty = 'mixed', language = 'he') => {
    setQuizLoading(true);
    setQuizType('gov-chapter');
    setQuizLanguage(language);
    setView('quiz');
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);

    try {
      const response = await axios.get(`${API}/questions/by-chapter/${examId}/${chapterId}`, {
        params: { difficulty, count: 30, language }
      });
      const questions = response.data;
      if (questions.length > 0) {
        setQuizQuestions(questions);
      } else {
        const fallback = await axios.get(`${API}/questions`, {
          params: { course_slug: examId, limit: 25 }
        });
        setQuizQuestions(fallback.data);
      }
    } catch (e) {
      console.error('Failed to load chapter questions:', e);
      try {
        const fallback = await axios.get(`${API}/questions`, {
          params: { course_slug: examId, limit: 25 }
        });
        setQuizQuestions(fallback.data);
      } catch (e2) {
        setError('לא ניתן לטעון שאלות כרגע');
      }
    } finally {
      setQuizLoading(false);
    }
  };

  // Start Medical Topic Quiz - quiz for specific medical topic
  const startMedicalTopicQuiz = async (topicId) => {
    setQuizLoading(true);
    setQuizType('medical-topic');
    setView('quiz');
    setQuizFinished(false);
    setCurrentQuestionIndex(0);
    setQuizScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);

    try {
      const response = await axios.get(`${API}/questions/medical-license`, {
        params: { 
          topic: topicId === 'all' ? null : topicId,
          count: 25 
        }
      });
      
      let questions = response.data;
      
      if (questions.length > 0) {
        setQuizQuestions(questions);
      } else {
        // Fallback to all medical questions
        const fallback = await axios.get(`${API}/questions/medical-license`, { params: { count: 25 } });
        setQuizQuestions(fallback.data);
      }
    } catch (e) {
      console.error('Failed to load medical topic questions:', e);
      setError('לא ניתן לטעון שאלות כרגע');
    } finally {
      setQuizLoading(false);
    }
  };

  // Answer question
  const answerQuestion = (answerIndex) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(answerIndex);
    const currentQ = quizQuestions[currentQuestionIndex];
    if (answerIndex === currentQ.correct_answer) {
      setQuizScore(prev => prev + 1);
    }
    setShowExplanation(true);
  };

  // Next question
  const nextQuestion = () => {
    if (currentQuestionIndex + 1 < quizQuestions.length) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizFinished(true);
    }
  };

  // Close quiz
  const closeQuiz = () => {
    // Return to course if we came from one, otherwise go home
    if (selectedCourse && selectedSpec) {
      setView('course');
    } else if (selectedSpec) {
      setView('specialization');
    } else {
      setView('home');
    }
    setQuizQuestions([]);
    setQuizFinished(false);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  // Filtered courses
  const filteredCourses = useMemo(() => {
    if (!selectedSpec?.courses) return [];
    return selectedSpec.courses.filter(course =>
      course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [selectedSpec, searchTerm]);

  // Render based on view
  if (view === 'promo') {
    return (
      <>
        <PromoView onStart={() => setView('register')} />
        <FloatingContactButton />
        <AIAssistant />
        <InstallAppBanner />
      </>
    );
  }

  if (view === 'register') {
    return (
      <>
        <RegisterView 
          onRegister={registerUser} 
          loading={loading}
          specializations={specializations}
          onLoadSpecs={fetchSpecializations}
        />
        <FloatingContactButton />
        <AIAssistant />
        <InstallAppBanner />
      </>
    );
  }

  // Bar Exam Mode Selection
  if (view === 'bar-mode-select') {
    return (
      <ExamModeSelector
        userName={user?.name || 'אורח'}
        onSelectMode={(mode) => {
          setAdaptiveMode(mode);
          setView('bar-exam');
        }}
      />
    );
  }

  // Bar Exam Simulator View (Full 100-question timed exam)
  if (view === 'bar-exam' && adaptiveMode === 'simulator') {
    return (
      <FullSimulator
        userId={user?.id || user?.name || 'guest'}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('bar-results');
        }}
        onBack={() => {
          setAdaptiveMode(null);
          setView('home');
        }}
      />
    );
  }

  // Bar Exam Adaptive/Diagnostic View
  if (view === 'bar-exam' && adaptiveMode) {
    return (
      <AdaptiveExamView
        userId={user?.id || user?.name || 'guest'}
        mode={adaptiveMode}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('bar-results');
        }}
        onBack={() => {
          setAdaptiveMode(null);
          setView('home');
        }}
      />
    );
  }

  // Bar Exam Results View
  if (view === 'bar-results' && adaptiveResults) {
    return (
      <ExamResultsView
        results={adaptiveResults}
        onBack={() => {
          setAdaptiveResults(null);
          setAdaptiveMode(null);
          if (selectedCourse && selectedSpec) {
            setView('course');
          } else if (selectedSpec) {
            setView('specialization');
          } else {
            setView('home');
          }
        }}
        onRetry={() => {
          setAdaptiveResults(null);
          setView('bar-mode-select');
        }}
      />
    );
  }

  // Bar Exam Dashboard - Enhanced
  if (view === 'bar-dashboard') {
    return (
      <EnhancedDashboard
        userId={user?.id || user?.name || 'guest'}
        onStartExam={(mode) => {
          setAdaptiveMode(mode);
          setView('bar-exam');
        }}
        onBack={() => setView('home')}
      />
    );
  }

  // Biology Talking Presentation
  if (view === 'biology-presentation') {
    return (
      <BiologyPresentation
        onBack={() => setView('home')}
      />
    );
  }

  // Medical License Simulator
  if (view === 'medical-simulator') {
    return (
      <MedicalSimulator
        userId={user?.id || user?.name || 'guest'}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('medical-results');
        }}
        onBack={() => setView('home')}
      />
    );
  }

  // Medical Results View
  if (view === 'medical-results' && adaptiveResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-950 p-6" dir="rtl">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center">
            <h1 className="text-3xl font-black text-white mb-6">תוצאות המבחן - רפואה</h1>
            
            <div className="text-8xl font-black mb-4">
              <span className={adaptiveResults.score >= 60 ? 'text-green-400' : 'text-red-400'}>
                {adaptiveResults.score}%
              </span>
            </div>
            
            <p className="text-xl text-slate-300 mb-8">
              {adaptiveResults.score >= 60 ? '🎉 עברת!' : '📚 צריך עוד תרגול'}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-green-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-400">{adaptiveResults.correct}</div>
                <div className="text-sm text-slate-400">נכונות</div>
              </div>
              <div className="bg-red-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-red-400">{adaptiveResults.wrong}</div>
                <div className="text-sm text-slate-400">שגויות</div>
              </div>
              <div className="bg-slate-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-slate-400">{adaptiveResults.unanswered}</div>
                <div className="text-sm text-slate-400">לא נענו</div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setAdaptiveResults(null);
                setView('home');
              }}
              className="bg-blue-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-400"
            >
              חזרה לדף הבית
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Nursing License Simulator
  if (view === 'nursing-simulator') {
    return (
      <NursingSimulator
        userId={user?.id || user?.name || 'guest'}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('nursing-results');
        }}
        onBack={() => setView('home')}
      />
    );
  }

  // Nursing Results View
  if (view === 'nursing-results' && adaptiveResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-teal-950 p-6" dir="rtl">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center">
            <h1 className="text-3xl font-black text-white mb-6">תוצאות המבחן - סיעוד</h1>
            
            <div className="text-8xl font-black mb-4">
              <span className={adaptiveResults.score >= 60 ? 'text-green-400' : 'text-red-400'}>
                {adaptiveResults.score}%
              </span>
            </div>
            
            <p className="text-xl text-slate-300 mb-8">
              {adaptiveResults.score >= 60 ? '🎉 עברת!' : '📚 צריך עוד תרגול'}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-green-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-400">{adaptiveResults.correct}</div>
                <div className="text-sm text-slate-400">נכונות</div>
              </div>
              <div className="bg-red-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-red-400">{adaptiveResults.wrong}</div>
                <div className="text-sm text-slate-400">שגויות</div>
              </div>
              <div className="bg-slate-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-slate-400">{adaptiveResults.unanswered}</div>
                <div className="text-sm text-slate-400">לא נענו</div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setAdaptiveResults(null);
                setView('home');
              }}
              className="bg-teal-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-teal-400"
            >
              חזרה לדף הבית
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dental License Simulator
  if (view === 'dental-simulator') {
    return (
      <DentalSimulator
        userId={user?.id || user?.name || 'guest'}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('dental-results');
        }}
        onBack={() => setView('home')}
      />
    );
  }

  // Dental Results View
  if (view === 'dental-results' && adaptiveResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-cyan-950 p-6" dir="ltr">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center">
            <h1 className="text-3xl font-black text-white mb-6">Exam Results - Dentistry</h1>
            
            <div className="text-8xl font-black mb-4">
              <span className={adaptiveResults.score >= 60 ? 'text-green-400' : 'text-red-400'}>
                {adaptiveResults.score}%
              </span>
            </div>
            
            <p className="text-xl text-slate-300 mb-8">
              {adaptiveResults.score >= 60 ? '🎉 You Passed!' : '📚 Keep Practicing'}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-green-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-400">{adaptiveResults.correct}</div>
                <div className="text-sm text-slate-400">Correct</div>
              </div>
              <div className="bg-red-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-red-400">{adaptiveResults.wrong}</div>
                <div className="text-sm text-slate-400">Wrong</div>
              </div>
              <div className="bg-slate-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-slate-400">{adaptiveResults.unanswered}</div>
                <div className="text-sm text-slate-400">Unanswered</div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setAdaptiveResults(null);
                setView('home');
              }}
              className="bg-cyan-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-cyan-400"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pharmacy License Simulator
  if (view === 'pharmacy-simulator') {
    return (
      <PharmacySimulator
        userId={user?.id || user?.name || 'guest'}
        onComplete={(results) => {
          setAdaptiveResults(results);
          setView('pharmacy-results');
        }}
        onBack={() => setView('home')}
      />
    );
  }

  // Pharmacy Results View
  if (view === 'pharmacy-results' && adaptiveResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-950 p-6" dir="ltr">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 text-center">
            <h1 className="text-3xl font-black text-white mb-6">Exam Results - Pharmacy</h1>
            
            <div className="text-8xl font-black mb-4">
              <span className={adaptiveResults.score >= 60 ? 'text-green-400' : 'text-red-400'}>
                {adaptiveResults.score}%
              </span>
            </div>
            
            <p className="text-xl text-slate-300 mb-8">
              {adaptiveResults.score >= 60 ? '🎉 You Passed!' : '📚 Keep Practicing'}
            </p>
            
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-green-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-400">{adaptiveResults.correct}</div>
                <div className="text-sm text-slate-400">Correct</div>
              </div>
              <div className="bg-red-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-red-400">{adaptiveResults.wrong}</div>
                <div className="text-sm text-slate-400">Wrong</div>
              </div>
              <div className="bg-slate-500/20 rounded-xl p-4">
                <div className="text-3xl font-bold text-slate-400">{adaptiveResults.unanswered}</div>
                <div className="text-sm text-slate-400">Unanswered</div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setAdaptiveResults(null);
                setView('home');
              }}
              className="bg-purple-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-purple-400"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'quiz') {
    return (
      <QuizView
        questions={quizQuestions}
        currentIndex={currentQuestionIndex}
        score={quizScore}
        selectedAnswer={selectedAnswer}
        showExplanation={showExplanation}
        finished={quizFinished}
        loading={quizLoading}
        quizType={quizType}
        language={quizLanguage}
        onAnswer={answerQuestion}
        onNext={nextQuestion}
        onClose={closeQuiz}
      />
    );
  }

  // Main App View
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" dir="rtl">
      {/* Floating Contact Us button - visible on all screens */}
      <FloatingContactButton />

      {/* PWA install invitation - shows for browsers/devices that can install */}
      <InstallAppBanner />

      {/* AI Assistant - available globally with course context if available */}
      <AIAssistant
        courseId={selectedCourse?.id}
        courseName={selectedCourse?.name}
      />

      {/* Admin Panel */}
      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
      
      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricing} 
        onClose={() => setShowPricing(false)}
        onSelectPlan={handleSelectPlan}
        user={isAuthenticated ? authUser : user}
      />
      
      {/* User Profile Modal */}
      <UserProfile
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        user={isAuthenticated ? authUser : user}
        onLogout={() => {
          localStorage.clear();
          window.location.href = '/';
        }}
      />
      
      {/* Subscription Banner */}
      {subscription?.type === 'trial' && subscription.daysLeft <= 10 && (
        <SubscriptionBanner 
          daysLeft={subscription.daysLeft}
          onUpgrade={() => setShowPricing(true)}
        />
      )}
      
      {/* Header */}
      <AppHeader
        user={user}
        authUser={authUser}
        isAuthenticated={isAuthenticated}
        subscription={subscription}
        onLogoClick={() => { setSelectedSpec(null); setSelectedCourse(null); setView('home'); }}
        onProfileClick={() => setShowProfile(true)}
        onPricingClick={() => setShowPricing(true)}
        onAdminClick={() => setShowAdminPanel(true)}
        appVersion={APP_VERSION}
      />

      {/* Main Content */}
      <main className="flex-1 p-6 max-w-7xl mx-auto w-full">
        {/* Home View */}
        {!selectedSpec && !selectedCourse && (
          <div className="space-y-8 animate-slide-up">
            {/* Welcome Header with greeting */}
            <div className="text-center py-6">
              {(user?.name || authUser?.name) && (
                <p className="text-slate-400 text-base mb-1" data-testid="user-greeting">
                  {new Date().getHours() < 12 ? 'בוקר טוב' : new Date().getHours() < 18 ? 'צהריים טובים' : 'ערב טוב'}, <span className="text-teal-500 font-semibold">{user?.name || authUser?.name}</span>
                </p>
              )}
              <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" data-testid="hero-title">
                <img 
                  src="/logo.png" 
                  alt="הצ'ייסר" 
                  className="h-12 sm:h-14 mx-auto object-contain select-none pointer-events-none"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </h1>
              <p className="text-slate-500 text-base mb-6">פלטפורמת הלמידה החכמה שלך</p>
              
              {/* Statistics - Dynamic */}
              <div className="flex justify-center gap-6 sm:gap-10 mb-4">
                <div className="text-center" data-testid="stat-courses">
                  <div className="text-2xl sm:text-3xl font-black text-teal-500">{dynamicStats?.total_courses || 244}</div>
                  <div className="text-xs sm:text-sm text-slate-500">קורסים</div>
                </div>
                <div className="text-center" data-testid="stat-questions">
                  <div className="text-2xl sm:text-3xl font-black text-teal-500">{dynamicStats?.total_questions ? `${(dynamicStats.total_questions / 1000).toFixed(1)}K+` : '85K+'}</div>
                  <div className="text-xs sm:text-sm text-slate-500">שאלות</div>
                </div>
                <div className="text-center" data-testid="stat-videos">
                  <div className="text-2xl sm:text-3xl font-black text-teal-500">+{dynamicStats?.total_videos || 498}</div>
                  <div className="text-xs sm:text-sm text-slate-500">סרטונים ומצגות</div>
                </div>
              </div>
            </div>

            {/* Continue Where You Left Off */}
            {user?.id && recentActivity && recentActivity.length > 0 && (
              <div className="mb-6" data-testid="continue-section">
                <h2 className="text-lg font-bold text-slate-700 mb-3 flex items-center gap-2 justify-end">
                  <span>המשך מאיפה שהפסקת</span>
                  <Clock className="w-5 h-5 text-teal-500" />
                </h2>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {recentActivity.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        const spec = specializations.find(s => s.id === item.specialization);
                        if (spec) {
                          setSelectedSpec(spec);
                          if (item.course_id) {
                            const course = spec.courses?.find(c => c.id === item.course_id);
                            if (course) {
                              setSelectedCourse(course);
                              setView('course');
                            }
                          }
                        }
                      }}
                      className="flex-shrink-0 bg-white border border-slate-200 hover:border-teal-300 hover:bg-teal-50 rounded-xl p-3 min-w-[180px] text-right transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                      data-testid={`recent-item-${idx}`}
                    >
                      <div className="text-sm font-bold text-slate-800 truncate">{item.course_name || item.exam_type || 'תרגול'}</div>
                      <div className="text-xs text-slate-400 mt-1">{item.score ? `ציון: ${item.score}%` : `${item.questions_answered || 0} שאלות`}</div>
                      <div className="text-xs text-teal-500 mt-1 font-medium">המשך ←</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contributors Wall */}
            <ContributorsWall />

            {/* Specializations Grid */}
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 border-r-4 border-teal-500 pr-4" data-testid="specializations-title">
              מסלולי לימוד
            </h2>
            
            {/* Specialization Cards - Unique colors per specialization */}
            <SpecializationGrid
              specializations={specializations}
              questionCounts={questionCounts}
              videoCounts={videoCounts}
              onSpecSelect={(spec) => setSelectedSpec(spec)}
            />

            {/* מבחני רישוי Section - Expandable with improved design */}
            {specializations.find(s => s.id === 'gov-exams') && (
              <div className="mb-8">
                <button
                  onClick={() => setShowLicenseExams(!showLicenseExams)}
                  className="w-full bg-gradient-to-l from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white py-4 px-6 rounded-2xl shadow-lg shadow-blue-500/20 hover:shadow-xl transition-all duration-300 flex items-center justify-between"
                  data-testid="license-exams-toggle"
                >
                  <div className={`text-xl transition-transform duration-300 ${showLicenseExams ? 'rotate-180' : ''}`}>
                    ▼
                  </div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold">מבחני רישוי ממשלתיים</h2>
                    <div className="text-2xl">🎓</div>
                  </div>
                </button>
                
                {/* Expandable Content */}
                {showLicenseExams && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 animate-slide-up">
                    {(specializations.find(s => s.id === 'gov-exams')?.exams || []).map((exam) => {
                      const stats = questionCounts[exam.id] || {};
                      const questionCount = stats.questions || exam.question_count || 0;
                      const isComingSoon = false;
                      return (
                        <button
                          key={exam.id}
                          onClick={() => {
                            if (isComingSoon) return;
                            if (exam.id === 'bar-exam') {
                              setView('bar-mode-select');
                            } else if (exam.id === 'medical-gov') {
                              setSelectedSpec({ ...specializations.find(s => s.id === 'gov-exams'), selectedExam: exam });
                              setView('medical-gov-exam');
                            } else if (exam.id === 'nursing-gov') {
                              setSelectedSpec({ ...specializations.find(s => s.id === 'gov-exams'), selectedExam: exam });
                              setView('nursing-gov-exam');
                            } else if (exam.id === 'dental-gov') {
                              setSelectedSpec({ ...specializations.find(s => s.id === 'gov-exams'), selectedExam: exam });
                              setView('dental-gov-exam');
                            } else if (exam.id === 'pharmacy-gov') {
                              setSelectedSpec({ ...specializations.find(s => s.id === 'gov-exams'), selectedExam: exam });
                              setView('pharmacy-gov-exam');
                            } else {
                              startQuiz('license-exam', 'mixed', exam.id);
                            }
                          }}
                          disabled={isComingSoon}
                          className={`bg-white border border-slate-200 p-5 rounded-2xl shadow-sm transition-all duration-300 text-center group relative ${isComingSoon ? 'opacity-60 cursor-not-allowed grayscale-[35%]' : 'hover:border-blue-400 hover:bg-blue-50 hover:shadow-lg active:scale-95 hover:-translate-y-1'}`}
                          data-testid={`gov-exam-${exam.id}`}
                          aria-disabled={isComingSoon}
                        >
                          {isComingSoon && (
                            <span
                              className="absolute top-2 right-2 z-20 bg-amber-400 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md tracking-wide"
                              data-testid={`gov-exam-coming-soon-${exam.id}`}
                            >
                              בקרוב
                            </span>
                          )}
                          <div className={`text-4xl mb-3 transition-transform duration-300 ${isComingSoon ? '' : 'group-hover:scale-110'}`}>{exam.icon}</div>
                          <div className="font-bold text-sm text-slate-800">{exam.name}</div>
                          {isComingSoon ? (
                            <div className="text-xs text-amber-700 font-medium mt-2">בפיתוח</div>
                          ) : (
                            <>
                              {questionCount > 0 && (
                                <div className="text-xs text-slate-500 mt-1">{questionCount.toLocaleString()} שאלות</div>
                              )}
                              {(exam.id === 'medical-gov' || exam.id === 'nursing-gov' || exam.id === 'dental-gov' || exam.id === 'pharmacy-gov') && (
                                <div className="text-xs text-blue-600 font-medium mt-2 bg-blue-50 rounded-full px-2 py-0.5 inline-block">סימולציה מלאה</div>
                              )}
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Dynamic Course Search - Improved */}
            <div className="bg-white p-5 rounded-2xl shadow-md border border-slate-100 mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="חפש קורס... (התחל להקליד)"
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-teal-500/20 focus:border-teal-500 outline-none pr-12 text-base font-medium transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="global-course-search"
                />
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              </div>
              
              {/* Dynamic Search Results */}
              {searchTerm.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                  {specializations.flatMap(spec => 
                    (spec.courses || [])
                      .filter(course => 
                        course.name.includes(searchTerm) || 
                        course.description?.includes(searchTerm)
                      )
                      .map(course => (
                        <button
                          key={`${spec.id}-${course.id}`}
                          onClick={() => { 
                            setSelectedSpec(spec); 
                            setSelectedCourse(course); 
                            setView('course');
                            setSearchTerm('');
                            loadCourseVideos(course.id);
                          }}
                          className="w-full p-4 bg-slate-50 hover:bg-teal-50 rounded-xl flex items-center justify-between group transition-all"
                          data-testid={`search-result-${course.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center text-xl">
                              {spec.icon}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">{course.name}</p>
                              <p className="text-sm text-slate-400">{spec.name}</p>
                            </div>
                          </div>
                          <ChevronLeft className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                        </button>
                      ))
                  )}
                  {searchTerm.length > 0 && specializations.flatMap(spec => 
                    (spec.courses || []).filter(course => 
                      course.name.includes(searchTerm) || 
                      course.description?.includes(searchTerm)
                    )
                  ).length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">לא נמצאו קורסים עבור "{searchTerm}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Government Exam Views - Using Generic Component */}
        {view === 'nursing-gov-exam' && (
          <GovExamView
            examType="nursing"
            exam={selectedSpec?.selectedExam}
            onBack={() => { setSelectedSpec(null); setView('home'); }}
            onStartQuiz={(type, difficulty, examId) => startQuiz(type, difficulty, examId)}
            onStartSimulator={(simulatorView) => setView(simulatorView)}
            onStartChapterQuiz={startGovChapterQuiz}
          />
        )}

        {view === 'dental-gov-exam' && (
          <GovExamView
            examType="dental"
            exam={selectedSpec?.selectedExam}
            onBack={() => { setSelectedSpec(null); setView('home'); }}
            onStartQuiz={(type, difficulty, examId) => startQuiz(type, difficulty, examId)}
            onStartSimulator={(simulatorView) => setView(simulatorView)}
            onStartChapterQuiz={startGovChapterQuiz}
          />
        )}

        {view === 'pharmacy-gov-exam' && (
          <GovExamView
            examType="pharmacy"
            exam={selectedSpec?.selectedExam}
            onBack={() => { setSelectedSpec(null); setView('home'); }}
            onStartQuiz={(type, difficulty, examId) => startQuiz(type, difficulty, examId)}
            onStartSimulator={(simulatorView) => setView(simulatorView)}
            onStartChapterQuiz={startGovChapterQuiz}
          />
        )}

        {view === 'medical-gov-exam' && (
          <GovExamView
            examType="medical"
            exam={selectedSpec?.selectedExam}
            onBack={() => { setSelectedSpec(null); setView('home'); }}
            onStartQuiz={(type, difficulty, examId) => startQuiz(type, difficulty, examId)}
            onStartSimulator={(simulatorView) => setView(simulatorView)}
            onStartChapterQuiz={startGovChapterQuiz}
          />
        )}

        {/* Dental Simulator */}

        {/* Courses List */}
        {selectedSpec && !selectedCourse && !view.includes('gov-exam') && view !== 'bar-mode-select' && (
          <div className="space-y-6 animate-slide-in-right">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setSelectedSpec(null)}
                className="text-slate-400 hover:text-slate-900 transition-colors font-bold flex items-center gap-2"
                data-testid="back-to-specs-button"
              >
                <ChevronLeft className="w-5 h-5" />
                חזרה למסלולים
              </button>
              <button 
                onClick={() => startQuiz('governmental', 'hard')}
                className="bg-rose-500 text-white px-4 py-2 rounded-xl font-bold text-xs shadow-md active:scale-95 transition-all"
                data-testid="spec-hard-exam-button"
              >
                מבחן מסכם 🔥
              </button>
            </div>

            <h2 className="text-4xl sm:text-6xl font-black text-slate-900" data-testid="spec-title">
              {selectedSpec.name}
            </h2>

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder={`חפש קורס ב${selectedSpec.name}...`}
                className="w-full p-5 bg-white border-2 border-slate-100 rounded-2xl shadow-lg focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 outline-none pr-12 text-lg font-medium transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="course-search-input"
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 w-6 h-6" />
            </div>

            {/* Courses Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
              {filteredCourses.map((course, index) => {
                const Icon = COURSE_ICONS[course.id] || BookOpen;
                const isComingSoon = course.coming_soon === true;
                return (
                  <button
                    key={course.id}
                    onClick={() => {
                      if (isComingSoon) return;
                      setSelectedCourse(course); setView('course'); loadCourseVideos(course.id);
                    }}
                    disabled={isComingSoon}
                    aria-disabled={isComingSoon}
                    className={`bg-white p-6 rounded-2xl shadow-sm border transition-all group text-right animate-slide-up ${
                      isComingSoon
                        ? 'opacity-60 cursor-not-allowed grayscale-[.4]'
                        : 'hover:shadow-xl cursor-pointer active:scale-[0.98]'
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`course-card-${course.id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6 text-teal-600" />
                      </div>
                      {isComingSoon ? (
                        <span
                          className="bg-amber-100 text-amber-700 text-[10px] font-black px-2 py-1 rounded-full border border-amber-300"
                          data-testid={`coming-soon-badge-${course.id}`}
                        >
                          בקרוב
                        </span>
                      ) : (
                        <ChevronLeft className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                      )}
                    </div>
                    <h4 className="text-xl font-black text-slate-900 mb-2">{course.name}</h4>
                    <p className="text-slate-400 text-sm line-clamp-2 mb-4">{course.description}</p>
                    <div className="flex items-center gap-4 text-xs font-bold text-slate-300">
                      {isComingSoon ? (
                        <span className="text-amber-700">🔒 התוכן יתווסף בקרוב</span>
                      ) : (
                        <>
                          <span>{questionCounts[course.id]?.total || course.question_count || 0} שאלות</span>
                          {videoCounts[course.id] > 0 && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                              <span className="text-teal-500">
                                {videoCounts[course.id]} סרטונים
                              </span>
                            </>
                          )}
                          <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                          <span>שנה {course.year}</span>
                        </>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Course Detail View */}
        {selectedCourse && view === 'course' && (
          <CourseDetailView
            course={selectedCourse}
            specId={selectedSpec?.id || 'nursing'}
            questionCounts={questionCounts}
            courseVideos={courseVideos}
            onBack={() => { setSelectedCourse(null); setView('home'); }}
            onStartQuiz={startQuiz}
            onStartChapterQuiz={startChapterQuiz}
            onAddVideos={handleAddVideo}
            onDeleteVideo={handleDeleteVideo}
            onFileUpload={handleFileUpload}
            onMoveVideo={handleMoveVideo}
            allCourses={specializations.flatMap(s => (s.courses || []))}
            activeVideo={activeVideo}
            setActiveVideo={setActiveVideo}
            showVideoUpload={showVideoUpload}
            setShowVideoUpload={setShowVideoUpload}
            newVideoData={newVideoData}
            setNewVideoData={setNewVideoData}
            uploadingVideo={uploadingVideo}
            uploadProgress={uploadProgress}
            userName={user?.name || authUser?.name || ''}
            userEmail={user?.email || authUser?.email || ''}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs font-bold text-slate-400">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
          <span className="text-slate-900">מערכת מלאה לסיעוד</span>
        </div>
        <div className="flex items-center gap-4 text-slate-500">
          <span>v{APP_VERSION}</span>
          <span>|</span>
          <span>סילבוס 2026</span>
        </div>
      </footer>
    </div>
  );
}

